import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "../axios";
import ArchiveModal from "../components/ArchiveModal";
import { getDayType, COL } from "../utils/dayType";
import { useDayTypeVersion } from "../components/DayTypeProvider";

const formatTime = (timeStr) => {
  if (!timeStr) return "";
  return timeStr.replace(/:/g, "").slice(0, 4);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${year.slice(-2)}/${month}/${day}`;
};

// 営業所の選択肢の「廃車車両」。全営業所の廃車車両を 1 つの表に並べる（行ごとに営業所を持つ）
const RETIRED = "__retired__";

// 廃車日の前日（最後に走れた日）までの 14 日
const retiredWindow = (retiredAt) => {
  const end = stepDate(retiredAt, -1);
  return [stepDate(end, -13), end];
};

const stepDate = (dateStr, delta) => {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const getDateArray = (start, end) => {
  const arr = [];
  let current = new Date(start);
  const last = new Date(end);
  while (current <= last) {
    const yyyy = current.getFullYear();
    const mm = String(current.getMonth() + 1).padStart(2, "0");
    const dd = String(current.getDate()).padStart(2, "0");
    arr.push(`${yyyy}-${mm}-${dd}`);
    current.setDate(current.getDate() + 1);
  }
  return arr;
};

const DateStepper = ({ value, onChange, hasError }) => {
  const borderColor = hasError ? "#fca5a5" : "#6ee7b7";
  const btnStyle = (isTop) => ({
    flex: 1,
    width: "20px",
    padding: 0,
    border: `1.5px solid ${borderColor}`,
    borderLeft: "none",
    ...(isTop ? { borderBottom: `0.75px solid ${borderColor}`, borderRadius: "0 6px 0 0" }
               : { borderTop:  `0.75px solid ${borderColor}`, borderRadius: "0 0 6px 0" }),
    background: "#fff",
    color: "#047857",
    fontSize: "11px",
    fontWeight: "700",
    lineHeight: 1,
    cursor: "pointer",
  });
  return (
    <div style={{ display: "flex", alignItems: "stretch" }}>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          border: `1.5px solid ${borderColor}`,
          borderRight: "none",
          borderRadius: "8px 0 0 8px",
          padding: "5px 8px",
          // iOS は 16px 未満だと自動でズームするので下回らせない
          fontSize: "16px",
          background: hasError ? "#fff5f5" : "#fff",
          color: "#065f46",
          outline: "none",
        }}
      />
      <div style={{ display: "flex", flexDirection: "column" }}>
        <button type="button" onClick={() => value && onChange(stepDate(value,  1))} style={btnStyle(true)}>＋</button>
        <button type="button" onClick={() => value && onChange(stepDate(value, -1))} style={btnStyle(false)}>−</button>
      </div>
    </div>
  );
};

function ArchivePage({ currentUser }) {
  // 特別日が API から届いたら描き直す（日付の色分けが getDayType に依存している）
  useDayTypeVersion();

  const [searchParams] = useSearchParams();
  const [allDepots, setAllDepots] = useState([]);
  // 営業所一覧の「廃車車両」カードからは ?retired=1 で来る
  const [depot, setDepot] = useState(searchParams.get("retired") === "1" ? RETIRED : "");
  const [data, setData] = useState([]);
  const [routeColors, setRouteColors] = useState({});
  const [modalData, setModalData] = useState({
    isOpen: false,
    bus_number: "",
    date: "",
    sightings: [],
    depot: "",
  });
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateError, setDateError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedBus, setHighlightedBus] = useState(null);
  const tableContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  // 廃車車両のリスト（GET /buses/retired）。廃車車両を選んだときだけ読む
  const [retiredList, setRetiredList] = useState(null);
  // 営業所一覧のカードからは ?list=1 で一覧を開いた状態で来る
  const [listOpen, setListOpen] = useState(searchParams.get("list") === "1");
  // 廃車車両一覧の中の車号検索（含む一致で絞り込む）
  const [listQuery, setListQuery] = useState("");
  // 廃車車両に切り替えたら、いちばん新しい廃車日の前日までの 2 週間を開く（URL で期間を渡したときは除く）
  const [retiredReady, setRetiredReady] = useState(false);
  const urlPeriod = useRef(false);
  // リストから選んだ車。表が読み込まれたらその行へ移る
  const pendingBus = useRef(null);

  const fetchDepots = useCallback(async () => {
    try {
      const res = await axios.get("/depots");
      setAllDepots(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchRoutes = useCallback(async (targetDepot) => {
    try {
      // 廃車車両はどの営業所の路線も出うるので、全営業所の路線から色を引く
      const res = await axios.get(targetDepot === RETIRED
        ? "/routes?all=true"
        : `/routes?depot=${encodeURIComponent(targetDepot)}&mode=archive`);
      const colorMap = {};
      res.data.forEach((r) => {
        colorMap[r.short_name] = r.color || "#9e9e9e";
      });
      setRouteColors(colorMap);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchSightings = useCallback(async (targetDepot, start, end) => {
    try {
      setLoading(true);
      const target = targetDepot === RETIRED ? "retired=1" : `depot=${encodeURIComponent(targetDepot)}`;
      const res = await axios.get(`/sightings-archive?${target}&start=${start}&end=${end}`);
      setData(res.data);
    } catch (e) {
      console.error(e);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDepots();
    // ?start=YYYY-MM-DD&end=YYYY-MM-DD で期間を渡せる
    const qs = searchParams.get("start"), qe = searchParams.get("end");
    if (/^\d{4}-\d{2}-\d{2}$/.test(qs || "") && /^\d{4}-\d{2}-\d{2}$/.test(qe || "")) {
      setStartDate(qs);
      setEndDate(qe);
      urlPeriod.current = true;
      return;
    }
    const today = new Date();
    const archiveEnd = new Date(today);
    archiveEnd.setDate(today.getDate() - 7);
    const archiveStart = new Date(archiveEnd);
    archiveStart.setDate(archiveEnd.getDate() - 6);
    setStartDate(
      `${archiveStart.getFullYear()}-${String(archiveStart.getMonth() + 1).padStart(2, "0")}-${String(archiveStart.getDate()).padStart(2, "0")}`
    );
    setEndDate(
      `${archiveEnd.getFullYear()}-${String(archiveEnd.getMonth() + 1).padStart(2, "0")}-${String(archiveEnd.getDate()).padStart(2, "0")}`
    );
  // 初回だけ読む（期間を動かしたあとで URL に戻されないように）
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDepots]);

  // 廃車車両: リストを読み、最初の期間を決める
  useEffect(() => {
    if (depot !== RETIRED) {
      setRetiredReady(false);
      return;
    }
    let cancelled = false;
    (retiredList ? Promise.resolve(retiredList) : axios.get("/buses/retired").then((r) => r.data))
      .then((list) => {
        if (cancelled) return;
        if (!retiredList) setRetiredList(list);
        if (urlPeriod.current) {
          urlPeriod.current = false;
        } else if (list.length > 0) {
          const latest = list.map((b) => b.retired_at).sort().at(-1);
          const [s, e] = retiredWindow(latest);
          setStartDate(s);
          setEndDate(e);
        }
        setRetiredReady(true);
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) setRetiredReady(true);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depot]);

  useEffect(() => {
    if (!depot) return;
    // 廃車車両は最初の期間が決まるまで読まない（二度読みを避ける）
    if (depot === RETIRED && !retiredReady) return;
    fetchRoutes(depot);
    if (startDate && endDate) {
      fetchSightings(depot, startDate, endDate);
    }
  }, [depot, startDate, endDate, retiredReady, fetchRoutes, fetchSightings]);

  // リストで選んだ車の行へ移る
  useEffect(() => {
    const bus = pendingBus.current;
    if (!bus || loading || !tableContainerRef.current) return;
    const row = tableContainerRef.current.querySelector(`tr[data-bus="${bus}"]`);
    if (row) {
      pendingBus.current = null;
      setHighlightedBus(bus);
      row.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [data, loading]);

  const pickRetired = (bus) => {
    const [s, e] = retiredWindow(bus.retired_at);
    pendingBus.current = bus.bus_number;
    setListOpen(false);
    setHighlightedBus(bus.bus_number);
    setStartDate(s);
    setEndDate(e);
  };

  useEffect(() => {
    if (!startDate || !endDate) return;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (start > end) {
      setDateError("開始日は終了日より後にできません");
    } else if ((end - start) / (1000 * 60 * 60 * 24) > 13) {
      setDateError("期間は最大14日までです");
    } else {
      setDateError("");
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (modalData.isOpen) return;
      // 廃車車両一覧のモーダルを開いているあいだは Esc で閉じるだけ
      if (listOpen) {
        if (e.key === "Escape") setListOpen(false);
        return;
      }
      if (e.key === "Escape") {
        setSearchQuery("");
        setHighlightedBus(null);
        searchInputRef.current?.blur();
        return;
      }
      if (/^\d$/.test(e.key) && document.activeElement !== searchInputRef.current) {
        e.preventDefault();
        setSearchQuery((prev) => prev + e.key);
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [modalData.isOpen, listOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    if (!tableContainerRef.current) return;
    const rows = tableContainerRef.current.querySelectorAll("tr[data-bus]");
    for (const row of rows) {
      if (row.dataset.bus.includes(q)) {
        setHighlightedBus(row.dataset.bus);
        row.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
    }
    setHighlightedBus(null);
  };

  const openModal = (bus_number, date, sightings, depotName) => {
    setModalData({ isOpen: true, bus_number, date, sightings, depot: depotName });
  };
  const closeModal = () => setModalData((prev) => ({ ...prev, isOpen: false }));

  const getMaxDisplay = () => currentUser?.max_sightings_display || Infinity;
  const dates = startDate && endDate ? getDateArray(startDate, endDate) : [];

  return (
    <div style={{ padding: "12px 8px", position: "relative" }}>

      {/* ── 日付バー（上部） ── */}
      <div style={{
        margin: "-12px -8px 8px",
        background: "linear-gradient(180deg, #d1fae5 0%, #ecfdf5 100%)",
        borderBottom: "2px solid #6ee7b7",
        boxShadow: "0 2px 10px rgba(4,120,87,0.12)",
        padding: "8px 12px",
      }}>
        <div style={{
          maxWidth: "640px",
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
        }}>
          <span style={{ fontSize: "11px", fontWeight: "700", color: "#047857", whiteSpace: "nowrap", letterSpacing: "0.04em" }}>開始</span>
          <DateStepper value={startDate} onChange={setStartDate} hasError={!!dateError} />
          <span style={{ fontSize: "13px", color: "#6ee7b7", fontWeight: "700" }}>〜</span>
          <span style={{ fontSize: "11px", fontWeight: "700", color: "#047857", whiteSpace: "nowrap", letterSpacing: "0.04em" }}>終了</span>
          <DateStepper value={endDate} onChange={setEndDate} hasError={!!dateError} />
          {dateError && (
            <span style={{ fontSize: "12px", color: "#dc2626", whiteSpace: "nowrap" }}>
              ⚠ {dateError}
            </span>
          )}
        </div>
      </div>

      {/* ── 廃車車両一覧（モーダル）。行を押すとその車の廃車前 2 週間の表へ ── */}
      {depot === RETIRED && retiredList && listOpen && (
        <div
          onClick={() => setListOpen(false)}
          style={{ position: "fixed", inset: 0, zIndex: 1500, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
        >
          <div
            role="dialog"
            aria-label="廃車車両一覧"
            onClick={(e) => e.stopPropagation()}
            style={{ width: "100%", maxWidth: "720px", maxHeight: "85vh", display: "flex", flexDirection: "column", background: "#fff", borderRadius: "12px", boxShadow: "0 10px 30px rgba(0,0,0,0.25)", overflow: "hidden" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#f3f4f6", borderBottom: "1px solid #e5e7eb" }}>
              <span style={{ fontSize: "14px", fontWeight: "700", color: "#374151" }}>廃車車両一覧（{retiredList.length}台）</span>
              <button type="button" onClick={() => setListOpen(false)} aria-label="閉じる"
                style={{ border: "none", background: "transparent", fontSize: "20px", lineHeight: 1, color: "#6b7280", cursor: "pointer" }}>×</button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 14px 0", flexWrap: "wrap" }}>
              <input
                type="text"
                value={listQuery}
                onChange={(e) => setListQuery(e.target.value.trim())}
                placeholder="車号で絞り込み"
                inputMode="numeric"
                aria-label="車号で絞り込み"
                style={{ width: "140px", border: "1.5px solid #d1d5db", borderRadius: "8px", padding: "4px 10px",
                         // iOS は 16px 未満だと自動でズームするので下回らせない
                         fontSize: "16px", outline: "none", color: "#374151" }}
              />
              {listQuery && (
                <button type="button" onClick={() => setListQuery("")}
                  style={{ border: "none", background: "transparent", color: "#6b7280", fontSize: "12px", cursor: "pointer" }}>クリア</button>
              )}
              <span style={{ fontSize: "11px", color: "#6b7280" }}>
                {listQuery ? `${retiredList.filter((b) => b.bus_number.includes(listQuery)).length}台が一致。` : ""}
                行を押すと、その車の廃車前 2 週間を表で開きます
              </span>
            </div>
            <div style={{ overflow: "auto", flex: 1 }}>
              <table style={{ width: "100%", minWidth: "460px", borderCollapse: "collapse", fontSize: "12px" }}>
                <thead>
                  <tr style={{ position: "sticky", top: 0, background: "#fff", boxShadow: "0 1px 0 #e5e7eb" }}>
                    {["車号", "営業所", "廃車日", "最終目撃", "件数"].map((h) => (
                      <th key={h} style={{ padding: "6px 8px", textAlign: h === "件数" ? "right" : "left", color: "#6b7280", fontWeight: "600", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {retiredList.filter((b) => !listQuery || b.bus_number.includes(listQuery)).map((b) => (
                    <tr
                      key={b.bus_number}
                      onClick={() => pickRetired(b)}
                      title="廃車前の 2 週間を表で開く"
                      style={{ cursor: "pointer", borderTop: "1px solid #f3f4f6", background: highlightedBus === b.bus_number ? "#fef9c3" : "transparent" }}
                    >
                      <td style={{ padding: "5px 8px", fontWeight: "700", color: "#374151" }}>{b.bus_number}</td>
                      <td style={{ padding: "5px 8px", color: "#6b7280", whiteSpace: "nowrap" }}>{b.depot}</td>
                      <td style={{ padding: "5px 8px", color: "#dc2626", whiteSpace: "nowrap" }}>{formatDate(b.retired_at)}</td>
                      <td style={{ padding: "5px 8px", color: "#374151" }}>
                        {b.last_seen_at ? (
                          <>
                            <span style={{ whiteSpace: "nowrap" }}>{formatDate(b.last_seen_at.slice(0, 10))} {b.last_seen_at.slice(11)}</span>
                            {b.last_route && (
                              <span style={{ marginLeft: "6px", fontSize: "11px", padding: "0 5px", borderRadius: "5px", color: "#fff", background: routeColors[b.last_route] || "#9e9e9e", whiteSpace: "nowrap" }}>
                                {b.last_route}
                              </span>
                            )}
                            {b.last_start && <span style={{ marginLeft: "4px", color: "#6b7280" }}>{b.last_start}</span>}
                          </>
                        ) : <span style={{ color: "#d1d5db" }}>—</span>}
                      </td>
                      <td style={{ padding: "5px 8px", textAlign: "right", color: "#6b7280" }}>{b.sightings_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── ローディング ── */}
      {loading && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "20px 16px",
          color: "#065f46",
          fontSize: "14px",
        }}>
          <div style={{
            width: 18,
            height: 18,
            border: "2.5px solid #a7f3d0",
            borderTopColor: "#059669",
            borderRadius: "50%",
            animation: "spin 0.7s linear infinite",
            flexShrink: 0,
          }} />
          読み込み中...
        </div>
      )}

      {/* ── 空状態（営業所未選択） ── */}
      {!loading && !depot && (
        <div style={{ padding: "32px 16px", textAlign: "center", color: "#6b7280", fontSize: "14px" }}>
          <svg width="48" height="40" viewBox="0 0 48 40" fill="none" stroke="#059669" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: "10px" }}>
            {/* 車体 */}
            <rect x="2" y="4" width="44" height="26" rx="3" />
            {/* 窓ライン上下 */}
            <line x1="2" y1="12" x2="46" y2="12" />
            <line x1="2" y1="22" x2="46" y2="22" />
            {/* 窓の仕切り */}
            <line x1="13" y1="12" x2="13" y2="22" />
            <line x1="24" y1="12" x2="24" y2="22" />
            <line x1="35" y1="12" x2="35" y2="22" />
            {/* タイヤ */}
            <circle cx="12" cy="34" r="4" />
            <circle cx="36" cy="34" r="4" />
          </svg>
          <div>右下の営業所から選択してください</div>
        </div>
      )}

      {/* ── 空状態（データなし） ── */}
      {!loading && data.length === 0 && depot && (
        <div style={{ padding: "32px 16px", textAlign: "center", color: "#6b7280", fontSize: "14px" }}>
          <div style={{ fontSize: "28px", marginBottom: "8px" }}>📋</div>
          {depot === RETIRED ? "この期間に走っていた廃車車両はありません" : "この期間の目撃情報はありません"}
        </div>
      )}

      {/* ── テーブル ── */}
      {dates.length > 0 && data.length > 0 && (
        <div ref={tableContainerRef} style={{ overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 196px)" }}>
          {/* 廃車車両は営業所をまたいで 1 つの表（車号の下に営業所を出す） */}
          {(depot === RETIRED ? ["廃車車両"] : Array.from(new Set(data.map((v) => v.depot)))).map((depotName) => (
            <div key={depotName} style={{ marginBottom: "32px" }}>

              {/* 営業所名ヘッダー */}
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                margin: "0 0 12px",
                padding: "11px 16px 11px 18px",
                borderLeft: "4px solid #047857",
                borderRadius: "0 8px 8px 0",
                background: "linear-gradient(90deg, #ecfdf5 0%, #f8fffe 70%, #fff 100%)",
                boxShadow: "0 1px 4px rgba(4,120,87,0.08)",
              }}>
                <span style={{
                  fontSize: "1.1rem",
                  fontWeight: "700",
                  color: "#065f46",
                  letterSpacing: "0.05em",
                }}>
                  {depotName}
                </span>
                <span style={{
                  fontSize: "10px",
                  fontWeight: "700",
                  color: "#6ee7b7",
                  letterSpacing: "0.15em",
                }}>
                  {depot === RETIRED ? `${data.length}台` : "営業所"}
                </span>
              </div>

              <table style={{
                borderCollapse: "collapse",
                width: "100%",
                textAlign: "center",
                tableLayout: "auto",
                fontSize: "13px",
              }}>
                <thead>
                  <tr>
                    <th style={{
                      padding: "8px 10px",
                      position: "sticky",
                      top: 0,
                      left: 0,
                      zIndex: 10,
                      background: "#047857",
                      color: "#fff",
                      fontWeight: "700",
                      fontSize: "12px",
                      letterSpacing: "0.04em",
                      borderRight: "2px solid #065f46",
                      borderBottom: "2px solid #065f46",
                    }}>
                      車号
                    </th>
                    {dates.map((date) => {
                      const cs = COL[getDayType(date)];
                      return (
                        <th key={date} style={{
                          padding: "8px 10px",
                          position: "sticky",
                          top: 0,
                          zIndex: 6,
                          background: cs.header,
                          color: "#fff",
                          fontWeight: "600",
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                          borderRight: `1px solid ${cs.hBorder}`,
                          borderBottom: `2px solid ${cs.hBorder}`,
                        }}>
                          {formatDate(date)}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {data
                    .filter((v) => depot === RETIRED || v.depot === depotName)
                    .map((bus, rowIdx) => (
                      <tr key={bus.bus_number}
                        data-bus={bus.bus_number}
                        style={{ background: rowIdx % 2 === 0 ? "#f0fdf4" : "#fff" }}
                      >
                        <td style={{
                          fontWeight: "700",
                          fontSize: "13px",
                          color: bus.retired_at ? "#6b7280" : "#047857",
                          background: highlightedBus === bus.bus_number ? "#fef08a" : rowIdx % 2 === 0 ? "#dcfce7" : "#ecfdf5",
                          padding: "6px 8px",
                          verticalAlign: "middle",
                          position: "sticky",
                          left: 0,
                          zIndex: 4,
                          borderRight: "2px solid #a7f3d0",
                          borderBottom: "1px solid #d1fae5",
                          whiteSpace: "nowrap",
                        }}>
                          {depot === RETIRED ? (
                            <>
                              <div>{bus.bus_number}</div>
                              <div style={{ fontSize: "10px", color: "#9ca3af", fontWeight: "400" }}>{bus.depot}</div>
                              <div style={{ fontSize: "9px", color: "#dc2626", fontWeight: "700", background: "#fee2e2", borderRadius: "3px", padding: "1px 3px", marginTop: "2px", display: "inline-block" }}>
                                廃車 {formatDate(bus.retired_at)}
                              </div>
                              <div style={{ fontSize: "9px", color: "#6b7280", fontWeight: "400", marginTop: "2px" }}>
                                最終 {bus.last_seen ? formatDate(bus.last_seen) : "—"}
                              </div>
                            </>
                          ) : bus.bus_number}
                        </td>
                        {dates.map((date) => {
                          const dayDataList = bus.sightings.filter((d) => d.date === date);
                          const records = dayDataList.flatMap((d) => d.records || []);
                          const grouped = {};
                          records.forEach((s) => {
                            const routeName = s.route || "";
                            if (!grouped[routeName]) grouped[routeName] = [];
                            grouped[routeName].push(`${s.start}${formatTime(s.time)}`);
                          });
                          Object.keys(grouped).forEach((r) =>
                            grouped[r].sort((a, b) => parseInt(a.slice(-4)) - parseInt(b.slice(-4)))
                          );
                          const maxCount = getMaxDisplay();
                          const cs = COL[getDayType(date)];
                          const baseBg = rowIdx % 2 === 0 ? cs.cellEven : cs.cellOdd;

                          return (
                            <td key={date}
                              onClick={() => openModal(bus.bus_number, date, records, bus.depot)}
                              style={{
                                verticalAlign: "top",
                                cursor: "pointer",
                                padding: "5px 6px",
                                background: baseBg,
                                borderRight: "1px solid #d1fae5",
                                borderBottom: "1px solid #d1fae5",
                                transition: "background 0.12s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = cs.hover;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = baseBg;
                              }}
                            >
                              {records.length > 0 ? (
                                Object.entries(grouped).map(([route, starts]) => {
                                  const displayStarts = starts.slice(0, maxCount);
                                  const overflowCount = starts.length - displayStarts.length;
                                  return (
                                    <div key={route} style={{ marginBottom: "4px" }}>
                                      <span style={{
                                        backgroundColor: routeColors[route] || "#9e9e9e",
                                        color: "#fff",
                                        padding: "1px 6px",
                                        borderRadius: "6px",
                                        display: "inline-block",
                                        marginBottom: "2px",
                                        fontSize: "11px",
                                        fontWeight: "600",
                                      }}>
                                        {route}
                                      </span>
                                      {displayStarts.map((s, idx) => (
                                        <div key={idx} style={{ fontSize: "12px", color: "#374151" }}>{s}</div>
                                      ))}
                                      {overflowCount > 0 && (
                                        <div style={{ fontSize: "11px", color: "#059669", fontWeight: "600" }}>
                                          +{overflowCount}件
                                        </div>
                                      )}
                                    </div>
                                  );
                                })
                              ) : (
                                <span style={{ color: "#d1d5db", fontSize: "12px" }}>—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      <ArchiveModal
        isOpen={modalData.isOpen}
        onClose={closeModal}
        // super_admin だけが編集できる。直したら閉じて表を読み直す
        // （過去データのキャッシュは本体が世代を進めて捨てている）
        onUpdated={(text) => {
          closeModal();
          setToastMessage(text);
          if (depot && startDate && endDate) fetchSightings(depot, startDate, endDate);
        }}
        currentUser={currentUser}
        depot={modalData.depot}
        bus_number={modalData.bus_number}
        date={modalData.date}
        sightings={modalData.sightings}
      />

      {toastMessage && (
        <div style={{
          position: "fixed",
          bottom: "30px",
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: "#1f2937",
          color: "#fff",
          padding: "10px 20px",
          borderRadius: "8px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.25)",
          zIndex: 2000,
          fontSize: "14px",
          animation: "fadeInOut 3s ease",
        }}>
          {toastMessage}
        </div>
      )}

      {/* ── フッター（営業所切替） ── */}
      <div style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: "linear-gradient(180deg, #ecfdf5 0%, #d1fae5 100%)",
        borderTop: "2px solid #6ee7b7",
        boxShadow: "0 -2px 10px rgba(4,120,87,0.12)",
      }}>
        <div style={{
          maxWidth: "640px",
          margin: "0 auto",
          padding: "8px 12px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}>
          {/* 車号検索 */}
          <form onSubmit={handleSearch} style={{ display: "flex", alignItems: "center", gap: "6px", flex: 1 }}>
            <span
              onClick={() => { setSearchQuery(""); setHighlightedBus(null); }}
              style={{ fontSize: "11px", fontWeight: "700", color: "#047857", letterSpacing: "0.04em", textAlign: "center", lineHeight: "1.3", userSelect: "none" }}
            >
              車号<br />検索
            </span>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); if (!e.target.value.trim()) setHighlightedBus(null); }}
              placeholder="1100"
              inputMode="numeric"
              style={{
                flex: "0 0 58px",
                minWidth: 0,
                maxWidth: "58px",
                border: "1.5px solid #6ee7b7",
                borderRadius: "8px",
                padding: "5px 10px",
                fontSize: "16px",
                outline: "none",
                background: "#fff",
                color: "#065f46",
              }}
            />
            <button type="submit" style={{
              background: "#047857",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              padding: "5px 12px",
              fontSize: "12px",
              fontWeight: "700",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}>
              移動
            </button>
          </form>

          {/* 廃車車両: 営業所の選択の代わりに一覧を開くボタン（営業所一覧のカードからだけ入る） */}
          {depot === RETIRED ? (
            <button
              type="button"
              onClick={() => setListOpen(true)}
              disabled={!retiredList}
              style={{ padding: "6px 12px", background: "#f3f4f6", border: "1.5px solid #d1d5db", borderRadius: "8px", cursor: "pointer", fontSize: "12px", fontWeight: "700", color: "#374151", whiteSpace: "nowrap" }}
            >
              廃車車両一覧{retiredList ? `（${retiredList.length}台）` : ""}
            </button>
          ) : (
          /* 営業所切替 */
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span style={{ fontSize: "11px", fontWeight: "700", color: "#047857", whiteSpace: "nowrap", letterSpacing: "0.04em" }}>
              営業所
            </span>
            <select
              value={depot}
              onChange={(e) => setDepot(e.target.value)}
              style={{
                border: "1.5px solid #6ee7b7",
                borderRadius: "8px",
                padding: "5px 8px",
                // iOS は 16px 未満だと自動でズームするので下回らせない
                fontSize: "16px",
                background: "#fff",
                color: "#065f46",
                fontWeight: "600",
                cursor: "pointer",
                maxWidth: "140px",
              }}
            >
              <option value="">-- 選択 --</option>
              {allDepots.map((d) => (
                <option key={d.short_name} value={d.short_name}>{d.short_name}</option>
              ))}
            </select>
          </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(10px) translateX(-50%); }
          10%, 90% { opacity: 1; transform: translateY(0) translateX(-50%); }
          100% { opacity: 0; transform: translateY(10px) translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

export default ArchivePage;
